import { computed, nextTick, onMounted, onUnmounted, provide, ref, watch } from "vue";
import Cookies from "js-cookie";
import { closeToast } from "vant";
import { badjsInit } from "~/utils/errorReporting";
import { useCmsDataStore } from "~/stores/cms-data";
import usePixel from "~/hooks/usePixel";
import { getChannel } from "~/utils/getChannel";
import eventBus, { EVENT_TYPES } from "~/utils/eventBus";
import { useLoginCallbackStore } from "~/stores/login-callback";
import { isInAppAsync } from "~/components/app.js";
import { useI18n } from "~/composables/useI18n";
import useLogin from "~/hooks/login/index";
import showMessage from "~/utils/showMessage";
import { markColdStartTagsPending } from "~/utils/coldStartTags";
import { stripLocalePrefix } from "~/utils/i18n-locale-routes";

export const useSiteShell = () => {
	const nuxtApp = useNuxtApp();
	const { $isMobile } = nuxtApp;
	const router = useRouter();
	const route = useRoute();
	const { locale, t } = useI18n();

	const pcFooterLang = computed(() => (locale?.value === "en" ? "en" : "zh-TW"));
	const routeType = $isMobile ? "m" : "pc";

	const showNav = computed(() => {
		const meta = route.meta;
		if (!meta) return !$isMobile;
		// SSR / 客户端都读页面 meta，避免 app-landing 等页先渲染全局头再隐藏造成双头闪烁
		return meta[`${routeType}-showNavBar`] !== false;
	});

	const isSticky = computed(() => {
		const meta = route.meta;
		if (!meta) return false;
		return Boolean(meta[`${routeType}-isSticky`]);
	});

	const showFooter = computed(() => {
		const meta = route.meta;
		if (!meta) return !$isMobile;
		return meta[`${routeType}-showFooter`] !== false;
	});

	const isConsultRoute = computed(() => {
		const path = stripLocalePrefix(route.path);
		return (
			path.startsWith("/seer/master-fortune") ||
			path.startsWith("/seer/consult") ||
			path.startsWith("/seer/chat/") ||
			path === "/seer/guide-chat"
		);
	});

	const stickyOffset = ref(0);
	const setStickyOffset = (value: number) => {
		stickyOffset.value = value;
	};

	const isInAppEnv = ref(false);
	const shouldMountSupportWidgets = ref(false);
	const showLogin = ref(false);
	const isLoggedIn = ref(false);
	const loginPresetData = ref<Record<string, any> | null>(null);
	const loginPresetVersion = ref(0);
	const loginTriggerSource = ref("page_action");
	const showEmailOfficail = ref(false);
	const backUrl = ref<string | null>(null);
	const channel = ref<string | null>(null);

	const cmsDataStore = useCmsDataStore();
	const { initPixel } = usePixel();

	const themeOverrides = {
		common: {
			primaryColor: "#c67f34",
			primaryColorHover: "#d68f44",
			primaryColorPressed: "#b65f24",
			primaryColorSuppl: "#fff7ee",
		},
	};

	const syncLoginStatus = () => {
		if (!import.meta.client) return;
		isLoggedIn.value = !!localStorage.getItem("token");
	};

	const clearThirdPartyLoginLoading = () => {
		if (!import.meta.client) return;
		if (sessionStorage.getItem("third_party_login_redirect")) {
			closeToast();
			sessionStorage.removeItem("third_party_login_redirect");
		}
	};

	let currentLoginCallback: (() => void) | null = null;

	const openLoginPopup = (
		callback?: () => void,
		presetData: any = null,
		isActiveLogin = true,
		triggerSource = "page_action"
	) => {
		if (typeof presetData === "boolean") {
			isActiveLogin = presetData;
			presetData = null;
		}

		// 有外部预填账号时允许打开（例如个人中心切换账号登录）
		if (import.meta.client && localStorage.getItem("token") && !presetData) return;

		loginTriggerSource.value = triggerSource || "page_action";
		loginPresetData.value = presetData;
		if (presetData) {
			loginPresetVersion.value += 1;
		}

		if (import.meta.client && !isActiveLogin) {
			useLoginCallbackStore().clearActiveLogin();
		} else if (import.meta.client && isActiveLogin) {
			useLoginCallbackStore().setActiveLogin();
		}

		if (currentLoginCallback) {
			eventBus.off(EVENT_TYPES.LOGIN_SUCCESS, currentLoginCallback);
		}

		if (callback && typeof callback === "function") {
			const handleLoginSuccess = () => {
				callback();
				syncLoginStatus();
				eventBus.off(EVENT_TYPES.LOGIN_SUCCESS, handleLoginSuccess);
				currentLoginCallback = null;
				loginPresetData.value = null;
			};
			eventBus.on(EVENT_TYPES.LOGIN_SUCCESS, handleLoginSuccess);
			currentLoginCallback = handleLoginSuccess;
		}

		void nextTick(() => {
			showLogin.value = true;
		});
	};

	watch(showLogin, newVal => {
		if (!newVal) {
			loginPresetData.value = null;
		}
	});

	const openEmailOfficail = () => {
		showEmailOfficail.value = true;
	};

	const openThirdPartyBindEmailPopup = () => {
		loginPresetData.value = null;
		loginTriggerSource.value = "third_party_bind";
		showLogin.value = true;
	};

	provide("openEmailOfficail", openEmailOfficail);
	provide("openLoginPopup", openLoginPopup);
	provide("setStickyOffset", setStickyOffset);
	provide("openLogin", () => {
		if (import.meta.client && localStorage.getItem("token")) return;
		showLogin.value = true;
		throw new Error("请登录");
	});

	const { afterLineAuthLogin, afterGoogleAuthLogin } = useLogin({
		invite_params: route.query.invite_params || null,
	});

	const pickQueryValue = (val: Record<string, unknown> | undefined, key: string) => {
		const raw = val?.[key];
		return Array.isArray(raw) ? raw[0] : raw;
	};

	const isSocialLoginCallback = (val: Record<string, unknown> | undefined) => {
		const code = pickQueryValue(val, "code");
		if (!code) return false;

		const loginType = String(
			pickQueryValue(val, "login_type") || pickQueryValue(val, "type") || ""
		);
		if (["google", "line", "facebook"].includes(loginType)) return true;

		// 授权回来时弹窗尚未挂载，依赖跳转前写入的标记识别回调
		return import.meta.client && sessionStorage.getItem("third_party_login_redirect") === "1";
	};

	let socialCallbackHandling = false;

	const finishSilentSocialLogin = async (redirectUri = "") => {
		showLogin.value = false;
		await showMessage(t("common.loginModal.loginSuccess"), "success");
		markColdStartTagsPending();
		syncLoginStatus();
		eventBus.emit(EVENT_TYPES.LOGIN_SUCCESS, { type: "reload", backUrl: backUrl.value });

		const targetPath = window.location.pathname;
		if (redirectUri) {
			try {
				const url = new URL(redirectUri);
				if (url.search) {
					const nextQuery: Record<string, string> = {};
					new URLSearchParams(url.search).forEach((value, key) => {
						nextQuery[key] = value;
					});
					await navigateTo({ path: targetPath, query: nextQuery }, { replace: true });
				}
			} catch {
				// ignore invalid redirect_uri
			}
		}

		const loginCallbackStore = useLoginCallbackStore();
		if (loginCallbackStore.isActiveLogin) {
			loginCallbackStore.setActiveLogin();
			return;
		}

		eventBus.emit("third-party-login-success", {
			type: "reload",
			backUrl: backUrl.value,
		});
	};

	const handleSocialLoginCallback = async (query: Record<string, unknown> | undefined) => {
		if (!import.meta.client || socialCallbackHandling || !isSocialLoginCallback(query)) return;

		const businessPath = stripLocalePrefix(route.path);
		if (businessPath === "/seer/login") return;

		const code = String(pickQueryValue(query, "code") || "");
		if (!code) return;

		// 换票前先取出 state，避免 URL 清理后丢失 redirect_uri
		let redirectUri = "";
		try {
			const stateStr = pickQueryValue(query, "state");
			if (stateStr) {
				redirectUri = JSON.parse(atob(String(stateStr)))?.redirect_uri || "";
			}
		} catch {
			// ignore invalid state
		}

		socialCallbackHandling = true;
		const loginType = String(
			pickQueryValue(query, "login_type") || pickQueryValue(query, "type") || "google"
		);

		try {
			const result =
				loginType === "line"
					? await afterLineAuthLogin(code, () => finishSilentSocialLogin(redirectUri))
					: await afterGoogleAuthLogin(code, () => finishSilentSocialLogin(redirectUri));

			// 需要绑定邮箱时打开绑定弹窗；成功路径不二次打开登录弹窗
			if (result?.status === "needs_bind_email") {
				showLogin.value = true;
			}
		} finally {
			socialCallbackHandling = false;
		}
	};

	watch(
		() => route.query,
		val => {
			const query = val as Record<string, unknown> | undefined;
			const gwLogin = pickQueryValue(query, "gwLogin");
			const fromLogin = pickQueryValue(query, "fromLogin");

			if (gwLogin === "1") {
				openLoginPopup(undefined, null, true, "url_param");
			}

			// OAuth 回跳在壳层静默换票，不二次打开登录弹窗
			void handleSocialLoginCallback(query);

			if (query?.backUrl) {
				backUrl.value = decodeURIComponent(String(pickQueryValue(query, "backUrl")));
			}

			if (fromLogin === "1" && !pickQueryValue(query, "code")) {
				clearThirdPartyLoginLoading();
			}
		},
		{ immediate: true }
	);

	const setBaseChannel = () => {
		const currentChannel = getChannel();
		channel.value = currentChannel;

		if (import.meta.client) {
			localStorage.setItem("channel", currentChannel);
		}

		Cookies.set("_channel", currentChannel);
	};

	const scheduleIdleTask = (task: () => void | Promise<void>, timeout = 1200) => {
		if (!import.meta.client) return;

		if ("requestIdleCallback" in window) {
			window.requestIdleCallback(
				() => {
					void task();
				},
				{ timeout }
			);
			return;
		}

		globalThis.setTimeout(() => {
			void task();
		}, timeout);
	};

	const handlePageShow = (event: PageTransitionEvent) => {
		if (event.persisted) clearThirdPartyLoginLoading();
	};

	const resetMobilePageScroll = () => {
		if (!import.meta.client) return;

		requestAnimationFrame(() => {
			const mobileLayout = document.querySelector(".mobile-layout");
			if (!(mobileLayout instanceof HTMLElement)) return;

			const hash = router.currentRoute.value.hash;
			if (hash) {
				try {
					const target = document.querySelector(hash);
					if (target instanceof HTMLElement) {
						target.scrollIntoView({ block: "start", behavior: "auto" });
						return;
					}
				} catch {}
			}

			mobileLayout.scrollTo({ top: 0, left: 0, behavior: "auto" });
		});
	};

	let removePageFinishHook: (() => void) | null = null;

	onMounted(() => {
		setBaseChannel();
		syncLoginStatus();

		if (import.meta.client) {
			localStorage.setItem("gw_header_and_footer", "show");
			window.addEventListener("pageshow", handlePageShow);
			window.addEventListener("storage", syncLoginStatus);
			eventBus.on(EVENT_TYPES.LOGIN_SUCCESS, syncLoginStatus);
			eventBus.on(EVENT_TYPES.THIRD_PARTY_NEEDS_BIND_EMAIL, openThirdPartyBindEmailPopup);
			removePageFinishHook = nuxtApp.hook("page:finish", resetMobilePageScroll);
		}

		scheduleIdleTask(async () => {
			shouldMountSupportWidgets.value = true;
			badjsInit();
			isInAppEnv.value = await isInAppAsync(3000);
			await cmsDataStore.updateCmsConfig();
			initPixel();
		});
	});

	onUnmounted(() => {
		if (currentLoginCallback) {
			eventBus.off(EVENT_TYPES.LOGIN_SUCCESS, currentLoginCallback);
			currentLoginCallback = null;
		}

		if (import.meta.client) {
			window.removeEventListener("pageshow", handlePageShow);
			window.removeEventListener("storage", syncLoginStatus);
			eventBus.off(EVENT_TYPES.LOGIN_SUCCESS, syncLoginStatus);
			eventBus.off(EVENT_TYPES.THIRD_PARTY_NEEDS_BIND_EMAIL, openThirdPartyBindEmailPopup);
		}

		removePageFinishHook?.();
		removePageFinishHook = null;
	});

	return {
		pcFooterLang,
		showNav,
		isSticky,
		stickyOffset,
		showFooter,
		isConsultRoute,
		isInAppEnv,
		shouldMountSupportWidgets,
		showLogin,
		isLoggedIn,
		loginPresetData,
		loginPresetVersion,
		loginTriggerSource,
		showEmailOfficail,
		backUrl,
		themeOverrides,
		openLoginPopup,
		openEmailOfficail,
	};
};
